import { useState } from "react";
import { Instagram, Mail, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { api, formatApiError, useContent } from "@/lib/api";

export default function Contact() {
  const content = useContent();
  const [form, setForm] = useState({ name: "", email: "", topic: "Coaching online", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Messaggio inviato. Ti risponderò al più presto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const email = content.contact_email || "serramartinacoaching@gmail.com";
  const instagram = content.instagram_url || "https://www.instagram.com/serramartinaaa/";

  return (
    <div data-testid="contact-page">
      <Nav />
      <section className="mx-auto max-w-7xl px-6 pb-28 pt-40 lg:px-12 lg:pt-52">
        <SectionLabel>Contatti</SectionLabel>
        <FadeUp as="h1" delay={0.1} className="mt-6 max-w-3xl font-display text-5xl leading-[1.05] text-ivory md:text-7xl" data-testid="contact-title">
          Parliamone<span className="italic text-mist">.</span>
        </FadeUp>
        <div className="mt-20 grid gap-16 lg:grid-cols-[1.2fr_1fr]">
          <FadeUp delay={0.2}>
            {sent ? (
              <div className="flex flex-col items-start gap-6 border border-sage bg-surface p-12" data-testid="contact-success">
                <p className="font-display text-3xl text-ivory">Messaggio inviato.</p>
                <p className="text-sm leading-relaxed text-mute">
                  Grazie per avermi scritto. Ti risponderò via email il prima possibile.
                </p>
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "Coaching online", message: "" }); }}
                  data-testid="contact-send-another" className="btn-outline">
                  Invia un altro messaggio
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-6" data-testid="contact-form">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="label-caps mb-3 block text-ivory/60" htmlFor="contact-name">Nome</label>
                    <input id="contact-name" data-testid="contact-name-input" required minLength={2} className="input-dark"
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Il tuo nome" />
                  </div>
                  <div>
                    <label className="label-caps mb-3 block text-ivory/60" htmlFor="contact-email">Email</label>
                    <input id="contact-email" type="email" data-testid="contact-email-input" required className="input-dark"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="la-tua@email.it" />
                  </div>
                </div>
                <div>
                  <label className="label-caps mb-3 block text-ivory/60" htmlFor="contact-topic">Argomento</label>
                  <select id="contact-topic" data-testid="contact-topic-select" className="input-dark bg-ink"
                    value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}>
                    <option>Coaching online</option>
                    <option>Call conoscitiva</option>
                    <option>Altro</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps mb-3 block text-ivory/60" htmlFor="contact-message">Messaggio</label>
                  <textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={6} className="input-dark resize-none"
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Raccontami i tuoi obiettivi o la tua richiesta..." />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit-button" className="btn-primary w-fit disabled:opacity-60">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <>Richiedi informazioni <Send size={14} /></>}
                </button>
              </form>
            )}
          </FadeUp>
          <FadeUp delay={0.3} className="flex flex-col gap-8">
            <div className="border border-line p-8">
              <p className="label-caps mb-4 text-mist">Scrivimi direttamente</p>
              <a href={`mailto:${email}`} data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm text-ivory/90 transition-colors duration-300 hover:text-mist">
                <Mail size={16} className="text-sage" /> {email}
              </a>
            </div>
            <div className="border border-line p-8">
              <p className="label-caps mb-4 text-mist">Instagram</p>
              <a href={instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link"
                className="flex items-center gap-3 text-sm text-ivory/90 transition-colors duration-300 hover:text-mist">
                <Instagram size={16} className="text-sage" /> @serramartinaaa
              </a>
            </div>
            <div className="border border-line p-8">
              <p className="label-caps mb-4 text-mist">WhatsApp</p>
              <a href={content.whatsapp_url || "https://wa.me/393248823603"} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                className="flex items-center gap-3 text-sm text-ivory/90 transition-colors duration-300 hover:text-mist">
                <MessageCircle size={16} className="text-sage" /> +39 324 882 3603
              </a>
            </div>
            <div className="border border-line bg-surface p-8">
              <p className="font-display text-xl text-ivory">Preferisci una call conoscitiva?</p>
              <p className="mt-3 text-sm leading-relaxed text-mute">
                20 minuti gratuiti per conoscerci e capire quale percorso fa per te.
              </p>
              <a href="/prenota-call" data-testid="contact-book-call" className="btn-primary mt-6 !px-6 !py-3">
                Prenota una call
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
      <Footer content={content} />
    </div>
  );
}
